import {createSelector} from '@reduxjs/toolkit'
import type {RootState} from '@containers/App/store'
import type {CustomDashboardInterface, WidgetConfig} from '@interfaces/customDashboard'
import {
  activeLayouts,
  activeTabIndex,
  activeWidgets,
} from '@containers/Dashboards/CustomDashboards/utils/dashboardTabs'

// The customDashboard slice is wrapped in redux-undo (AB#6079), which nests
// its actual state under `.present` (with `.past`/`.future` beside it for the
// undo/redo stacks). Centralized here so that indirection lives in one place
// instead of every call site spelling out `.customDashboard.present...`.
export const selectCustomDashboard = (state: RootState): RootState['customDashboard']['present'] =>
  state.customDashboard.present

export const selectActiveDashboard = (state: RootState): CustomDashboardInterface | null =>
  selectCustomDashboard(state).activeDashboard

export const selectEditMode = (state: RootState): boolean => selectCustomDashboard(state).editMode

export const selectUnsavedChanges = (state: RootState): boolean =>
  selectCustomDashboard(state).unsavedChanges

/**
 * The unsaved edits GlobalUnsavedChangesGuard warns about before the page
 * unloads: a dashboard in edit mode with unsaved changes. The proactive
 * re-authorize (AB#6617) skips its redirect while this holds.
 */
export const selectHasGuardedUnsavedChanges = (state: RootState): boolean =>
  selectUnsavedChanges(state) && selectEditMode(state)

export const selectSelectedWidgetId = (state: RootState): string | null =>
  selectCustomDashboard(state).selectedWidgetId

export const selectWidgetLibraryOpen = (state: RootState): boolean =>
  selectCustomDashboard(state).widgetLibraryOpen

export const selectCanUndo = (state: RootState): boolean => state.customDashboard.past.length > 0

export const selectCanRedo = (state: RootState): boolean => state.customDashboard.future.length > 0

/** The open sheet's id (AB#6448): a valid tab id, or null without tabs. */
export const selectActiveTabId = (state: RootState): string | null => {
  const dashboard = selectActiveDashboard(state)
  if (!dashboard) return null
  const index = activeTabIndex(dashboard, selectCustomDashboard(state).activeTabId)
  return dashboard.tabs?.[index]?.id ?? null
}

/** The grid of the open sheet (the whole dashboard's without tabs). */
export const selectActiveLayouts = createSelector(
  [selectActiveDashboard, selectActiveTabId],
  (dashboard, tabId): CustomDashboardInterface['layouts'] =>
    dashboard ? activeLayouts(dashboard, tabId) : {lg: []},
)

/** The open sheet's widgets only (every widget without tabs). */
export const selectActiveTabWidgets = createSelector(
  [selectActiveDashboard, selectActiveTabId],
  (dashboard, tabId): Record<string, WidgetConfig> =>
    dashboard ? activeWidgets(dashboard, tabId) : {},
)
